/* ==========================================================================
   AMBASSADOR BUNDLES — carousel controls
   ==========================================================================
   Loaded only when the section's layout_mode is 'carousel'.

   WHAT THIS DOES AND DELIBERATELY DOES NOT DO
   The scroller itself is native CSS (scroll-snap + overflow-x in
   bundles-carousel.css). Touch dragging, trackpad swiping, momentum,
   keyboard arrow-scrolling and scroll-snap all work with no JavaScript at all,
   so if this file fails to load the row still swipes.

   What a pointer device lacks is a swipe gesture. So this file adds exactly
   two things and nothing more:
     1. prev/next buttons, for mouse and keyboard users;
     2. a progress bar, because the scrollbar is hidden for design reasons and
        the viewer still needs to know how much row is left.

   It does NOT re-implement scrolling, snapping or lazy loading.

   SCOPED BY CLASS, NOT BY A SHARED DATA ATTRIBUTE
   The homepage already renders a Shop by Category carousel whose markup uses
   data-carousel-nav / -prev / -next. Two sections sharing one hook is a latent
   bug the moment either script is edited, so every lookup here is anchored to
   .bundles-sec--carousel and the bundles-* classes.
   ========================================================================== */

(function () {
  "use strict";

  var REDUCED = window.matchMedia
    ? window.matchMedia("(prefers-reduced-motion: reduce)")
    : null;

  function motionOK() {
    return !(REDUCED && REDUCED.matches);
  }

  function initCarousel(root) {
    var list = root.querySelector(".bundles-sec__grid");
    var nav = root.querySelector(".bundles-carousel__nav");
    if (!list || !nav) return;

    var prev = nav.querySelector(".bundles-carousel__btn--prev");
    var next = nav.querySelector(".bundles-carousel__btn--next");
    var track = nav.querySelector(".bundles-carousel__progress");
    var thumb = nav.querySelector(".bundles-carousel__progress-thumb");

    /* RTL flips the visual meaning of "previous" and "next". The theme ships
       [dir=rtl] rules, so read the direction rather than assume LTR. */
    var rtl =
      (document.documentElement.getAttribute("dir") || "").toLowerCase() ===
      "rtl";

    function overflow() {
      return list.scrollWidth - list.clientWidth;
    }

    /* One "page" is the width of one card plus one gap, multiplied by how many
       cards are currently visible. Scrolling by a whole number of cards is what
       makes each move land exactly on a snap point instead of between two. */
    function step() {
      var first = list.querySelector(".bundles-sec__item");
      if (!first) return list.clientWidth;
      var cs = getComputedStyle(list);
      var gap = parseFloat(cs.columnGap || cs.gap || "0") || 0;
      var itemW = first.getBoundingClientRect().width + gap;
      if (!itemW) return list.clientWidth;
      var visible = Math.max(1, Math.round((list.clientWidth + gap) / itemW));
      return itemW * visible;
    }

    function paint() {
      var max = overflow();
      var canScroll = max > 2;

      /* Arrows are shown only when there is somewhere to go. This section holds
         a handful of bundles, so on a wide screen they all fit at once and the
         whole nav is hidden rather than left as dead controls under the row. */
      nav.hidden = !canScroll;
      if (!canScroll) {
        if (prev) prev.disabled = true;
        if (next) next.disabled = true;
        if (thumb) thumb.style.width = "0px";
        return;
      }

      var pos = rtl ? -list.scrollLeft : list.scrollLeft;

      if (prev) prev.disabled = pos <= 1;
      if (next) next.disabled = pos >= max - 1;

      if (track && thumb) {
        var tw = track.clientWidth;
        var frac = list.clientWidth / list.scrollWidth;
        var thumbW = Math.max(24, Math.round(tw * frac));
        var travel = Math.max(0, tw - thumbW);
        var ratio = max > 0 ? Math.min(1, Math.max(0, pos / max)) : 0;
        thumb.style.width = thumbW + "px";
        /* Only transform moves, so this never triggers layout on scroll. */
        thumb.style.transform =
          "translateX(" + Math.round(travel * ratio) + "px)";
      }
    }

    /* Scroll fires far more often than a repaint is useful, so coalesce to one
       paint per frame. */
    var queued = false;
    function onScroll() {
      if (queued) return;
      queued = true;
      requestAnimationFrame(function () {
        queued = false;
        paint();
      });
    }

    function go(dir) {
      var d = rtl ? -dir : dir;
      var target = list.scrollLeft + d * step();
      var max = overflow();
      if (target < 0) target = 0;
      if (target > max) target = max;
      list.scrollTo({
        left: target,
        behavior: motionOK() ? "smooth" : "auto",
      });
    }

    if (prev) {
      prev.addEventListener("click", function () {
        go(-1);
      });
    }
    if (next) {
      next.addEventListener("click", function () {
        go(1);
      });
    }

    /* touchstart as well as scroll: on some touch browsers pointer events are
       retargeted during a drag, so listening for scroll alone can lag the
       progress bar by a frame at the start of a swipe. */
    list.addEventListener("touchstart", onScroll, { passive: true });
    list.addEventListener("scroll", onScroll, { passive: true });

    if (window.ResizeObserver) {
      var ro = new ResizeObserver(onScroll);
      ro.observe(list);
    } else {
      window.addEventListener("resize", onScroll);
    }

    /* Images settle after load and change scrollWidth. The cards use lazy
       loading, so without this the progress bar starts out the wrong length and
       the next button can be enabled when the row is already at its end. */
    window.addEventListener("load", paint);
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(paint).catch(function () {});
    }

    /* The theme editor re-renders a section on every setting change; repaint so
       the controls match the new content. */
    document.addEventListener("shopify:section:load", function (event) {
      if (event.target && event.target.contains(root)) paint();
    });

    paint();
  }

  function initAll(scope) {
    (scope || document)
      .querySelectorAll(".bundles-sec--carousel")
      .forEach(initCarousel);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }
})();
